import type { MeetingInsightFixture, MeetingReviewDetail } from './types';

const INSIGHT_TYPES = new Set<MeetingInsightFixture['type']>([
  'buying_signal',
  'upsell_opportunity',
  'client_concern',
  'competitor_mention'
]);

function normalizeInsightType(
  type: unknown
): MeetingInsightFixture['type'] {
  const normalized = String(type ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (INSIGHT_TYPES.has(normalized as MeetingInsightFixture['type'])) {
    return normalized as MeetingInsightFixture['type'];
  }
  if (normalized.includes('buy')) return 'buying_signal';
  if (normalized.includes('upsell') || normalized.includes('opportunity')) {
    return 'upsell_opportunity';
  }
  if (normalized.includes('competitor')) {
    return 'competitor_mention';
  }
  return 'client_concern';
}

function defaultLabelForType(type: MeetingInsightFixture['type']): string {
  if (type === 'buying_signal') return 'Buying signal';
  if (type === 'upsell_opportunity') return 'Upsell opportunity';
  if (type === 'competitor_mention') return 'Competitor mention';
  return 'Client concern';
}

/**
 * Map API / analysis insight payloads onto the FE MeetingInsightFixture shape.
 * Handles missing ids and alternate field names so Relationship Insights is not blank.
 */
export function normalizeMeetingInsights(
  raw: unknown
): MeetingInsightFixture[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry, index) => {
      const item = entry as Record<string, unknown>;
      const type = normalizeInsightType(item.type);
      const label = String(item.label ?? '').trim() || defaultLabelForType(type);
      const description = String(
        item.description ?? item.body ?? item.summary ?? ''
      ).trim();
      const id = String(
        item.id ??
          item.external_id ??
          item.transcript_message_external_id ??
          `insight-${index + 1}`
      );
      const transcriptMessageId = String(
        item.transcript_message_id ??
          item.transcriptMessageId ??
          item.transcript_message_external_id ??
          ''
      ).trim();

      return {
        id,
        type,
        label,
        description,
        ...(transcriptMessageId
          ? { transcriptMessageId }
          : undefined)
      } satisfies MeetingInsightFixture;
    })
    .filter((insight) => insight.label.length > 0 || insight.description.length > 0);
}

/** Ensure meeting analysis/detail always has a usable insights array. */
export function normalizeMeetingDetailInsights(
  detail: MeetingReviewDetail & Record<string, unknown>
): MeetingReviewDetail {
  const raw =
    detail.insights ??
    detail.relationship_insights ??
    (detail as { analysis?: { insights?: unknown } }).analysis?.insights;

  return {
    ...detail,
    insights: normalizeMeetingInsights(raw)
  };
}
